import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NEVER } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { fadeIn, fadeOut, slideDown, slideUp } from 'src/app/animations';
import { SiteNote } from 'src/app/models/site-notes/site-note.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { SiteNotesService } from 'src/app/services/sections/site-notes/site-notes.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ButtonIcons } from '../../action-button/models/button-icons.enum';
import { ToastType } from '../../toast/models/toast-type.enum';
import { Item } from 'src/app/models/item.model';
import { SiteNoteRequest, UpdateSiteNoteDto } from 'src/app/models/site-notes/site-note-request.model';
import { SiteNoteAddTemplateComponent } from '../site-note-add-template/site-note-add-template.component';
import { GenericModalComponent } from '../../generic-modal/generic-modal.component';
import { RequestUpdateEntity } from '../../job-details/request-update/model/request-update-entity.enum';
import { UserAccountInfo } from 'src/app/models/user-account-info.model';
import { SiteNoteAssignee } from 'src/app/models/site-notes/site-note-assignee.model';
import { AuthService } from 'src/app/services/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ResolvedSiteNote } from 'src/app/resolvers/site-notes/models/resolved-site-note.model';
import { SiteNoteResourceType } from 'src/app/models/site-notes/site-note-resource-type.model';

@Component({
	selector: 'site-note-add',
	templateUrl: './site-note-add.component.html',
	styleUrls: ['./site-note-add.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss'],
	animations: [slideUp(100), slideDown(100), fadeIn(200), fadeOut(200)]
})
export class SiteNoteAddComponent implements AfterViewInit, OnDestroy, OnInit {
	@ViewChild('siteNoteAdd') set content(content: SiteNoteAddTemplateComponent) {
		if (content) { // initially setter gets called with undefined
			this.siteNoteAddTemplate = content;
		}
	}

	@ViewChild('submitConfirmationModal', { static: true }) public submitConfirmationModal: GenericModalComponent;
	public self = this;

	private siteNoteAddTemplate: SiteNoteAddTemplateComponent;

	public isLoading = false;
	public isVendor = false;

	public deficienciesUrl: string;
	public myWorkUrl: string;
	public siteNotesUrl: string;

	public isEditMode: boolean = false;
	public siteNote: SiteNote = null;
	public accountSiteId: number= null;
	public siteNoteAddRequest: SiteNoteRequest;
	public siteNoteEditRequest: UpdateSiteNoteDto;
	public jobId: number;
	public possibleAssignees: SiteNoteAssignee[] = [];
	public accountInfo: UserAccountInfo = null;

	public isAddPhotosVisible = false;
	private isAnyPhotoExceedMaxSize = false;
	private files = [];
	public photosBadge: { hasBadge?: boolean, value?: string } = {};
	public photosWarningMessage = '';
	public isSaveButtonEnabled: boolean;

	public readonly ButtonIcons = ButtonIcons;
	public haveAccess: boolean | null = null;

	public assetTypes: SiteNoteResourceType[] = [];
	public locationTypes: SiteNoteResourceType[] = [];

	constructor(
		private route: ActivatedRoute,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private siteNotesService: SiteNotesService,
		private authService: AuthService
	) { }

	ngOnInit(): void {
		this.accountInfo = this.activatedRoute.snapshot.data.accountInfo;

		const resolvedSiteNote = this.activatedRoute.snapshot.data.siteNote as ResolvedSiteNote;
		if(!!resolvedSiteNote) {
			this.siteNote = this.activatedRoute.snapshot.data.siteNote.data;
			this.accountSiteId = this.siteNote.accountSiteId;
		} else {
			this.accountSiteId = this.accountInfo?.accountSiteInfo?.accountSiteId;
		}
		this.isEditMode = !this.route.snapshot.paramMap?.get('siteNoteId');
		this.jobId = parseInt(this.activatedRoute.snapshot.paramMap.get('jobId'), 10);
		if (this.siteNote) {
			this.checkIfHaveAccess();
			this.isSaveButtonEnabled = true;
		} else {
			this.checkIfCanCreate();
		}
		this.getSiteNoteAssignees(this.jobId);

		this.getTypes();
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			GlobalSettingsService.isGlobalLoaderLoading = false;
			//GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, 0);
	}

	ngOnDestroy(): void { }

	private checkIfHaveAccess() {
		this.siteNotesService.isSiteNoteAccessibleByUser(TokenHelper.getToken(this.activatedRoute), this.siteNote.siteNoteId).subscribe(res => {
			this.haveAccess = res;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, () => {			
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		});
	}

	private checkIfCanCreate() {
		this.siteNotesService.canCreateSiteNotes(TokenHelper.getToken(this.activatedRoute), this.jobId).subscribe(res => {
			this.haveAccess = res;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}, () => {
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		});
	}

	public addSiteNote(siteNoteRequest: SiteNoteRequest, photos: any): void {
		this.isLoading = true;
		if (siteNoteRequest.assignedUserId === 0) {
			siteNoteRequest.assignedUserId = null;
		}
		this.siteNotesService.addSiteNote(TokenHelper.getToken(this.activatedRoute), siteNoteRequest, photos)
			.pipe(catchError(() => {
				this.isLoading = false;
				ToastService.pushToast({
					text: 'Could not add Site Note. Please try again!',
					icon: 'error',
					type: ToastType.Error
				});
				return NEVER;
			}))
			.subscribe(result => {
				this.router.navigate(['../'], { relativeTo: this.activatedRoute });
				this.isLoading = false;
			});
	}

	public onSiteNoteAddSave(siteNoteRequest: SiteNoteRequest): void {
		this.siteNoteAddRequest = siteNoteRequest;
	}

	public onSiteNoteEditSave(updateSiteNoteDto: UpdateSiteNoteDto): void {
		this.siteNoteEditRequest = updateSiteNoteDto;
	}

	public onBackClick(): void {
		this.router.navigate(['../'], { relativeTo: this.activatedRoute });
	}

	public getSiteNoteAssignees(jobId): void {
		this.siteNotesService.getSiteNoteAssignees(TokenHelper.getToken(this.activatedRoute), jobId).subscribe(res => {
			this.possibleAssignees = res;
			this.possibleAssignees.unshift({ userAccountId: 0, fullName: '-None-' });
		}, error => {
			ToastService.pushToast({
				text: 'Could not get Assignees. Please try again!',
				icon: 'error',
				type: ToastType.Error
			});
		});
	}

	public getTypes(): void {
		this.siteNotesService.getSiteNoteAssetTypes(TokenHelper.getToken(this.activatedRoute), this.accountSiteId, true, true).subscribe(
			res => {
				this.locationTypes = res.filter(rt => rt.isLocation);
				this.assetTypes = res.filter(rt => rt.isAsset);
			}, error => {
			ToastService.pushToast({
				text: 'Could not get types. Please try again!',
				icon: 'error',
				type: ToastType.Error
			});
		}
		)
	}

	public onSaveClick(): void {
		if (!this.isAnyPhotoExceedMaxSize && this.files.length <= 10) {
			this.saveNote();
		}
		else {
			if (this.files.length > 10) {
				this.photosWarningMessage = `Depending on your internet connection speed, you may experience delays due to the number of files (${this.files.length}) selected.`;
			}
			if (this.isAnyPhotoExceedMaxSize) {
				this.photosWarningMessage = `Depending on your internet connection speed, you may experience delays due to the file size of the selected files.`;
			}
			if (this.files.length > 10 && this.isAnyPhotoExceedMaxSize) {
				this.photosWarningMessage = `Depending on your internet connection speed, you may experience delays due to the number of files (${this.files.length}) selected and file size.`;
			}
			this.submitConfirmationModal.open();

		}
	}

	private saveNote(): void {
		this.siteNoteAddTemplate.getSiteNote();
		const photos = [];
		if (!!this.siteNote) {
			this.updateSiteNote(this.siteNoteEditRequest, this.files);
		} else {
			this.addSiteNote(this.siteNoteAddRequest, this.files);
		}
	}

	public updateSiteNote(siteNoteRequest: UpdateSiteNoteDto, photos: any): void {
		this.isLoading = true;
		if (siteNoteRequest.assignedUserId === 0) {
			siteNoteRequest.assignedUserId = null;
		}
		this.siteNotesService.updateSiteNote(TokenHelper.getToken(this.activatedRoute), siteNoteRequest, photos).subscribe(res => {
			this.router.navigate(['../'], { relativeTo: this.activatedRoute });
			this.isLoading = false;
		}, (error: HttpErrorResponse) => {
			this.isLoading = false;
			if (error.status === 401) {
				ToastService.pushToast({
					text: 'Feature locked due to insufficient permissions!',
					icon: 'error',
					type: ToastType.Error
				});
			} else {
				ToastService.pushToast({
					text: 'Could not update Site Note. Please try again!',
					icon: 'error',
					type: ToastType.Error
				});
			}
		});
	}

	public enableSaveButton(isSaveButtonEnabled: boolean): void {
		this.isSaveButtonEnabled = isSaveButtonEnabled;
	}

	public openAddPhotos(): void {
		this.isAddPhotosVisible = true;
	}

	public onCloseAddPhotos(event: { files: any[], isVisible: boolean, isAnyPhotoExceedMaxSize: boolean }): void {
		this.isAddPhotosVisible = event.isVisible;
		this.files = event.files;
		this.isAnyPhotoExceedMaxSize = event.isAnyPhotoExceedMaxSize;
		this.photosBadge = { hasBadge: this.files.length > 0, value: this.files.length.toString() };
	}

	public onClickAddPhotos(): void {
		this.openAddPhotos();
	}

	public onSaveConfirmed(): void {
		this.saveNote();
		this.submitConfirmationModal.hide();
	}
}
